/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import AutoComplete from '@components/Atoms/AutoComplete';
import TextField from '@components/Atoms/TextField';
import Tooltip from '@components/Atoms/Tooltip';
import { SubjectRecord } from '@components/ContentFilters/types';
import { wordsSearch } from './utils/helpers';
import { QuestionType, SupportedQuestionTypes } from './types';
import { useGetQGSSupportedQuestionTypes } from './hooks';

export const getFilteredQuestionTypesBySubject = (
  supportedQuestionTypes: SupportedQuestionTypes | null,
  selectedSubject?: SubjectRecord,
) => {
  if (!supportedQuestionTypes || !selectedSubject) {
    return [];
  }

  if (selectedSubject?.slug === 'ela') {
    return supportedQuestionTypes?.english || [];
  }

  if (Object.keys(supportedQuestionTypes).includes(selectedSubject?.slug)) {
    return supportedQuestionTypes[selectedSubject?.slug];
  }

  return supportedQuestionTypes?.default || [];
};

const QuestionTypeSelector = ({ handleChange, isDisabled, selectedSubject }) => {
  const [supportedQuestionTypes, setSupportedQuestionTypes] =
    useState<SupportedQuestionTypes | null>(null);
  const { mutate: fetchSupportedQuestionTypes } = useGetQGSSupportedQuestionTypes();
  const [selectedQuestionType, setSelectedQuestionType] =
    useState<QuestionType | null>(null);

  useEffect(() => {
    fetchSupportedQuestionTypes(undefined, {
      onSuccess: (data) => {
        setSupportedQuestionTypes(data);
      },
    });
  }, [fetchSupportedQuestionTypes]);

  const filteredQuestionTypes = React.useMemo(
    () => getFilteredQuestionTypesBySubject(supportedQuestionTypes, selectedSubject),
    [supportedQuestionTypes, selectedSubject],
  );

  // Set the first question type as the default selected question type
  // when the filteredQuestionTypes change
  useEffect(() => {
    const firstQuestionType = filteredQuestionTypes?.[0];
    setSelectedQuestionType(firstQuestionType);
    handleChange(firstQuestionType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredQuestionTypes]);

  const handleQuestionTypeChange = (_, value) => {
    setSelectedQuestionType(value as unknown as QuestionType);
    handleChange(value);
  };

  return (
    <AutoComplete
      options={filteredQuestionTypes}
      getOptionLabel={(option) => option.name}
      title="Question Types"
      data-testid="question-type-selector"
      filterOptions={wordsSearch}
      onChange={handleQuestionTypeChange}
      groupBy={(option) => option.categoryName}
      value={selectedQuestionType || null}
      disableClearable
      noOptionsText="No question types currently supported"
      size="small"
      disabled={isDisabled}
      isOptionEqualToValue={(option, value) => option.type === value.type}
      renderOption={(props, option) => (
        <Tooltip title={option.description} arrow placement="right" key={option.type}>
          <li {...props}>{option.name}</li>
        </Tooltip>
      )}
      sx={{ mt: '0.5rem' }}
      renderInput={(params) => (
        <TextField
          label="Question Type"
          variant={undefined}
          onChange={undefined}
          value={undefined}
          {...params}
          placeholder="Search for question type..."
          InputProps={{
            ...params.InputProps,
            style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
        />
      )}
    />
  );
};

export default QuestionTypeSelector;
